import React, { useState } from "react";
import JSZip from "jszip";
import "../../css/ImportDocument.css";
import "../../css/DocumentationHomeComponent.css";

function UploadZipFile({ onNext, onBack, enhancedRightClickEnabled, showContextMenu, ContextMenuComponent }) {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [zipFile, setZipFile] = useState(null);
    const [extractedData, setExtractedData] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");


    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type === "application/zip" || file.name.endsWith(".zip")) {
                setZipFile(file);
                setErrorMessage("");
            } else {
                setErrorMessage("Please select a valid ZIP file.");
                setZipFile(null);
            }
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.type === "application/zip" || file.name.endsWith('.zip')) {
                setZipFile(file);
                setErrorMessage("");
            } else {
                setErrorMessage("Please select a valid ZIP file.");
                setZipFile(null);
            }
        }
    };

    const extractZipFile = async () => {
        if (!zipFile) {
            setErrorMessage("Please select a ZIP file first.");
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {
            const zip = new JSZip();
            const zipData = await zip.loadAsync(zipFile);

            const files = [];
            const images = [];
            const structure = {};

            // Extract all files from the ZIP
            for (const [relativePath, zipEntry] of Object.entries(zipData.files)) {
                if (!zipEntry.dir) {
                    const fileName = relativePath.split('/').pop();
                    const filePath = relativePath;

                    if (fileName.toLowerCase().endsWith('.html')) {
                        // Extract HTML content
                        const content = await zipEntry.async("text");
                        files.push({
                            name: fileName,
                            path: filePath,
                            content: content,
                            directory: relativePath.substring(0, relativePath.lastIndexOf('/')) || 'root'
                        });
                    } else if (fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|svg|webp)$/)) {
                        // Only extract images from the attachments directory
                        if (relativePath.toLowerCase().includes('attachments/')) {
                            // Extract image as base64
                            const content = await zipEntry.async("base64");
                            const mimeType = getMimeType(fileName);
                            images.push({
                                name: fileName,
                                path: filePath,
                                content: `data:${mimeType};base64,${content}`,
                                directory: relativePath.substring(0, relativePath.lastIndexOf('/')) || 'root'
                            });
                        }
                    }
                }
            }

            // Build directory structure for preview
            files.forEach(file => {
                const pathParts = file.path.split('/');
                let currentLevel = structure;

                pathParts.forEach((part, index) => {
                    if (index === pathParts.length - 1) {
                        // This is the file
                        if (!currentLevel.files) currentLevel.files = [];
                        currentLevel.files.push({
                            name: part,
                            fullPath: file.path,
                            content: file.content
                        });
                    } else {
                        // This is a directory
                        if (!currentLevel[part]) {
                            currentLevel[part] = {};
                        }
                        currentLevel = currentLevel[part];
                    }
                });
            });

            const extractedData = {
                totalFiles: files.length,
                totalImages: images.length,
                files: files,
                images: images,
                structure: structure,
                originalFileName: zipFile.name
            };

            setExtractedData(extractedData);
            console.log('Extracted ZIP data:', extractedData);

        } catch (error) {
            console.error('Error extracting ZIP file:', error);
            setErrorMessage("Failed to extract ZIP file. Please ensure it's a valid ZIP file containing HTML documents.");
        } finally {
            setIsLoading(false);
        }
    };

    const getMimeType = (fileName) => {
        const extension = fileName.toLowerCase().split('.').pop();
        const mimeTypes = {
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'bmp': 'image/bmp',
            'svg': 'image/svg+xml',
            'webp': 'image/webp'
        };
        return mimeTypes[extension] || 'image/jpeg';
    };

    const handleNext = () => {
        // Validate that a ZIP file is selected
        if (!zipFile) {
            alert("Please select a ZIP file to upload.");
            return;
        }

        // If file is selected but not extracted yet, extract it first
        if (!extractedData) {
            extractZipFile();
            return;
        }

        // If extracted, proceed to next step
        if (extractedData && onNext) {
            setIsTransitioning(true);
            setTimeout(() => {
                onNext(extractedData);
            }, 150);
        }
    };

    const handleBackClick = () => {
        if (onBack) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        }
    };

    const renderStructurePreview = (structure, level = 0) => {
        return Object.keys(structure).map(key => {
            if (key === 'files') {
                return structure.files.map((file, index) => (
                    <div key={`file-${index}`} style={{ marginLeft: `${level * 20}px`, color: '#4a90e2' }}>
                        📄 {file.name}
                    </div>
                ));
            } else {
                return (
                    <div key={key}>
                        <div style={{ marginLeft: `${level * 20}px`, fontWeight: 'bold', color: '#f39c12' }}>
                            📁 {key}
                        </div>
                        {renderStructurePreview(structure[key], level + 1)}
                    </div>
                );
            }
        });
    };

    if (isLoading) {
        return (
            <div 
                className="ImportDocument__main-div"
                onContextMenu={
                    enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                        }
                    : undefined
                }
            >
                <div className="ImportDocument__content">
                    <div className="ImportDocument__loading-animation-div">
                        <i className="fas fa-spinner fa-spin DocumentationHomeComponent__loading-animation-icon"></i>
                    </div>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    return (
        <div 
            className="ImportDocument__main-div"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            <div className="ImportDocument__content">
                <h2>Import Confluence Space</h2>
                
                <p className="medium" style={{ marginTop: "20px" }}>
                    Upload a ZIP file containing your exported Confluence space. The ZIP should contain HTML files and any associated images.
                </p>

                {/* File upload area with drag and drop */}
                <div 
                    style={{ 
                        border: '2px dashed var(--bb-text)', 
                        borderRadius: '8px', 
                        padding: '40px', 
                        textAlign: 'center', 
                        marginTop: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.3s ease'
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <p style={{ marginBottom: "15px" }}>
                        {zipFile ? `Selected: ${zipFile.name}` : "Drag and drop your ZIP file here, or click to browse"}
                    </p>
                    <input 
                        type="file" 
                        accept=".zip,application/zip" 
                        onChange={handleFileChange}
                        style={{ 
                            marginTop: '10px',
                            padding: '8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid var(--bb-text)',
                            borderRadius: '4px',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                        }} 
                    />
                    {zipFile && (
                        <div style={{ 
                            marginTop: "10px", 
                            color: "green", 
                            fontWeight: "bold",
                            fontSize: "14px"
                        }}>
                            <p>✓ File ready for extraction</p>
                            <p style={{ fontSize: "12px", color: "var(--bb-text)", marginTop: "5px" }}>
                                Size: {(zipFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                        </div>
                    )}
                </div>

                {errorMessage && (
                    <div style={{ 
                        marginTop: "15px", 
                        padding: "10px", 
                        backgroundColor: "#ffebee", 
                        border: "1px solid #f44336", 
                        borderRadius: "4px",
                        color: "#d32f2f"
                    }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: "8px" }}></i>
                        {errorMessage}
                    </div>
                )}

                {zipFile && !extractedData && (
                    <div style={{ 
                        marginTop: "15px", 
                        padding: "10px", 
                        backgroundColor: "rgba(76, 175, 80, 0.1)", 
                        border: "1px solid #4CAF50", 
                        borderRadius: "4px",
                        color: "#2E7D32",
                        textAlign: "center"
                    }}>
                        <p style={{ margin: "0", fontWeight: "bold" }}>
                            ✓ ZIP file selected! Click the right arrow to extract and continue.
                        </p>
                    </div>
                )}

                {extractedData && (
                    <div style={{ 
                        marginTop: "30px", 
                        padding: "20px", 
                        backgroundColor: "rgba(255, 255, 255, 0.1)", 
                        borderRadius: "8px",
                        border: "1px solid var(--bb-text)"
                    }}>
                        <h3 style={{ marginBottom: "15px", color: "var(--bb-text)" }}>
                            Extraction Complete! ✓
                        </h3>
                        
                        <div style={{ 
                            display: "flex", 
                            justifyContent: "space-around", 
                            marginBottom: "20px",
                            flexWrap: "wrap"
                        }}>
                            <div style={{ 
                                textAlign: "center", 
                                minWidth: "120px",
                                marginBottom: "10px"
                            }}>
                                <div style={{ 
                                    fontSize: "24px", 
                                    fontWeight: "bold", 
                                    color: "var(--bb-text)" 
                                }}>
                                    {extractedData.totalFiles}
                                </div>
                                <div style={{ 
                                    fontSize: "14px", 
                                    color: "var(--bb-text)", 
                                    opacity: "0.8" 
                                }}>
                                    HTML Files
                                </div>
                            </div>
                            <div style={{ 
                                textAlign: "center", 
                                minWidth: "120px",
                                marginBottom: "10px"
                            }}>
                                <div style={{ 
                                    fontSize: "24px", 
                                    fontWeight: "bold", 
                                    color: "var(--bb-text)" 
                                }}>
                                    {extractedData.totalImages}
                                </div>
                                <div style={{ 
                                    fontSize: "14px", 
                                    color: "var(--bb-text)", 
                                    opacity: "0.8" 
                                }}>
                                    Images
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ 
                                marginBottom: "10px", 
                                color: "var(--bb-text)",
                                fontSize: "16px"
                            }}>
                                File Structure Preview:
                            </h4>
                            <div style={{ 
                                maxHeight: "200px", 
                                overflowY: "auto",
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                padding: "15px",
                                borderRadius: "4px",
                                border: "1px solid var(--bb-text)",
                                fontSize: "14px"
                            }}>
                                {renderStructurePreview(extractedData.structure)}
                            </div>
                        </div>

                        <div style={{ textAlign: "center" }}>
                            <p style={{ 
                                marginBottom: "15px", 
                                color: "var(--bb-text)",
                                fontSize: "16px",
                                fontWeight: "bold"
                            }}>
                                ✓ Ready to configure your space import settings! Click the right arrow to continue.
                            </p>
                        </div>
                    </div>
                )}

                <div style={{ marginTop: "25px" }}>
                    <p className="medium">
                        <strong>Instructions for exporting from Confluence:</strong>
                    </p>
                    <ol style={{ 
                        marginTop: "15px", 
                        paddingLeft: "20px",
                        color: "var(--bb-text)"
                    }}>
                        <li style={{ marginBottom: "8px" }}>
                            Navigate to your space in Confluence. You should see your space name, "All Content", and 
                            "Space Settings" on the top left.
                        </li>
                        <li style={{ marginBottom: "8px" }}>
                            Click on "Space Settings".
                        </li>
                        <li style={{ marginBottom: "8px" }}>
                            Under "General", click on "Export space".
                        </li>
                        <li style={{ marginBottom: "8px" }}>
                            Choose the <strong>HTML format option</strong>, select whether you want to export all content or specific content, and 
                            then click "Export Space".
                        </li>
                        <li style={{ marginBottom: "8px" }}>
                            You should now have a ZIP file downloaded to your computer. Upload that ZIP file above.
                        </li>
                        <li style={{ marginBottom: "8px" }}>
                            <em>Note: The ZIP file will contain HTML files and an attachments directory with images - we'll process both automatically!</em>
                        </li>
                    </ol>
                </div>
            </div>

            {/* Back Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--back" 
                onClick={handleBackClick}
                onContextMenu={
                    enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                        }
                    : undefined
                }
                title="Go back"
                aria-label="Go back to previous step"
            >
                ❮
            </div>

            {/* Forward Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--forward" 
                onClick={handleNext}
                onContextMenu={
                    enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                        }
                    : undefined
                }
                title="Continue to next step"
                aria-label="Continue to next step"
            >
                ❯
            </div>

            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default UploadZipFile;
